/**
 * ParticleField — the canvas layer. One per surface:
 *   kind="ceremony" (cap 60/30/15 by tier) or kind="ambient" (20/10/5).
 *
 * Renders a full-bleed canvas inside its (position:relative) parent, runs the
 * pooled ParticleEngine on rAF, and exposes an imperative handle with the
 * presets. Reduced motion: spawn counts are divided by 3 (prototype rule).
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { CSSProperties } from "react";
import { ParticleEngine } from "./engine.js";
import type { ParticleFieldHandle, ParticleInit } from "./engine.js";
import { burst, confetti, ember, EMBER_INTERVAL_MS, puff, ring } from "./presets.js";
import type { PresetSpec } from "./presets.js";
import type { ParticleKind } from "../service.js";
import { useJuiceService } from "../context.jsx";

export type { ParticleFieldHandle } from "./engine.js";

export interface ParticleFieldProps {
  kind: ParticleKind;
  className?: string;
  style?: CSSProperties;
  /** z-index — comps put the fx canvas at 40 (tokens.z.fxCanvas). */
  zIndex?: number;
}

export const ParticleField = forwardRef<ParticleFieldHandle, ParticleFieldProps>(
  function ParticleField({ kind, className, style, zIndex = 40 }, handleRef) {
    const service = useJuiceService();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const engineRef = useRef<ParticleEngine | null>(null);
    const emberTimer = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const engine = new ParticleEngine(service.particleCap(kind));
      engineRef.current = engine;

      const size = () => {
        const parent = canvas.parentElement;
        canvas.width = parent ? parent.clientWidth : canvas.clientWidth;
        canvas.height = parent ? parent.clientHeight : canvas.clientHeight;
      };
      size();
      const g = globalThis as unknown as {
        addEventListener?: (t: string, cb: () => void) => void;
        removeEventListener?: (t: string, cb: () => void) => void;
      };
      g.addEventListener?.("resize", size);

      const ctx = canvas.getContext("2d");
      let raf = 0;
      const loop = () => {
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          engine.step();
          engine.render(ctx);
        }
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);

      return () => {
        cancelAnimationFrame(raf);
        g.removeEventListener?.("resize", size);
        if (emberTimer.current !== null) clearInterval(emberTimer.current);
        emberTimer.current = null;
        engineRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind, service]);

    useImperativeHandle(
      handleRef,
      (): ParticleFieldHandle => {
        const dims = () => {
          const c = canvasRef.current;
          return { w: c ? c.width : 0, h: c ? c.height : 0 };
        };
        const emit = (spec: PresetSpec) => {
          const engine = engineRef.current;
          if (!engine) return;
          // Reduced motion: n/3, prototype-verbatim.
          const n = service.reducedMotion() ? Math.floor(spec.count / 3) : spec.count;
          engine.spawn(n, spec.factory);
        };
        return {
          burst: () => {
            const { w, h } = dims();
            emit(burst(w, h));
          },
          confetti: () => {
            const { w } = dims();
            emit(confetti(w));
          },
          puff: () => {
            const { w, h } = dims();
            emit(puff(w, h));
          },
          ring: (cx, cy, col) => {
            const { w, h } = dims();
            emit(ring(cx ?? w / 2, cy ?? h / 2, Math.random, col));
          },
          embersStart: () => {
            if (emberTimer.current !== null) return;
            emberTimer.current = setInterval(() => {
              if (service.reducedMotion()) return; // prototype: no ambient embers reduced
              const { w, h } = dims();
              const spec = ember(w, h);
              engineRef.current?.spawn(spec.count, spec.factory);
            }, EMBER_INTERVAL_MS);
          },
          embersStop: () => {
            if (emberTimer.current !== null) clearInterval(emberTimer.current);
            emberTimer.current = null;
          },
          spawn: (count, factory) => {
            engineRef.current?.spawn(count, factory);
          },
          clear: () => engineRef.current?.clear(),
          get engine() {
            return engineRef.current;
          },
        };
      },
      [service],
    );

    return (
      <canvas
        ref={canvasRef}
        className={className}
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex,
          ...style,
        }}
        aria-hidden="true"
      />
    );
  },
);
